import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  List,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';
import SEO from '../components/SEO';
import { generateOrganizationSchema } from '../utils/schemaGenerators';

import FiftyFiftyCustody from '../assets/images/5050-custody.png';
import AppealableTemporaryOrders from '../assets/images/appealable-temporary-orders.png';
import CamerasInCourtroom from '../assets/images/cameras-in-courtrooms.png';
import ChildSupportReform from '../assets/images/child-support-dual-calculation-reform.png';
import BestInterestChild from '../assets/images/best-interests-child-courtroom-1000.png';
import ChildSupportIntegrity from '../assets/images/child-support-integrity-act.png';
import FamilyRightsDueProcess from '../assets/images/family-rights-due-process-act.png';
import JusticeDelayedJusticeDenied from '../assets/images/justice-delayed-is-justice-denied.png';
import LitigantAccessEconomy from '../assets/images/litigant-access-economy-act.png';
import ParentChildRestoration from '../assets/images/parent-child-restoration-act.png';
import JudicialAccountabilityReferral from '../assets/images/judicial-accountability-referral-act.png';
import AffiliateLogoThree from '../assets/images/logo-john-mast.png';
import AffiliateLogoTwo from '../assets/images/logo-npo.jpg';
import AffiliateLogoOne from '../assets/images/logo-texans-for-judicial-accountability.png';
import ReimbursementFees from '../assets/images/reimbursement-fees-parental-alienation.png';
import HeadShot from '../assets/images/robert-garza-headshot-2.jpg';
import HeadShot3 from '../assets/images/robert-garza-headshot-3.png';
import ThreeStrikes from '../assets/images/three-strikes.png';
import TimeTakenTimeBack from '../assets/images/time-taken-time-back.png';
import DonationDialog from '../components/DonationDialog';
import HeroCarousel from '../components/HeroCarousel';
import { trackScheduleCall, trackEvent } from '../components/GoogleAnalytics';
import NavigateToButton from '../components/NavigateToButton';
import JoinTheTeamDialog from '../components/JoinTheTeamDialog';

// Lazy load heavy components
const CalendlyDialog = React.lazy(() => import('../components/CalendlyDialog'));

const Home: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [donationDialogOpen, setDonationDialogOpen] = useState(false);
  const [calendlyDialogOpen, setCalendlyDialogOpen] = useState(false);
  const [joinTeamDialogOpen, setJoinTeamDialogOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  // Handler for donation dialog
  const handleDonationDialogClose = () => {
    setDonationDialogOpen(false);
  };

  // Handler for Calendly dialog
  const handleCalendlyDialogOpen = () => {
    trackScheduleCall('Home');
    setCalendlyDialogOpen(true);
  };

  const handleCalendlyDialogClose = () => {
    setCalendlyDialogOpen(false);
  };

  const handleJoinTeamDialogOpen = () => {
    setJoinTeamDialogOpen(true);
  };

  const handleJoinTeamDialogClose = () => {
    setJoinTeamDialogOpen(false);
  };

  return (
    <Container component="main" maxWidth={false}>
      <SEO
        title="RobertGarza.us | Family Court Reform & Equal Parenting"
        description="Robert Garza is bringing equality to family court through critical justice reforms. Learn about his legislative platform and comprehensive family law bills."
        path="/"
        schema={generateOrganizationSchema()}
      />
      {/* Hero and case review banner grow with their content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: { md: 'calc(100dvh - 64px)' },
          width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' },
          ml: { xs: '-16px', sm: '-24px' },
          overflow: 'hidden',
        }}
      >
        <HeroCarousel onCalendlyOpen={handleCalendlyDialogOpen} onJoinTeamOpen={handleJoinTeamDialogOpen} />

        {/* Schedule a Coaching Call banner */}
        <Box
          sx={(t) => {
            const gold = t.palette.primary.main;
            const bg = t.palette.mode === 'dark' ? '#000000' : '#ffffff';
            return {
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 3, md: 10 },
              py: { xs: 2, md: 2 },
              backgroundColor: bg,
              borderTop: `1px solid ${alpha(gold, 0.25)}`,
              backgroundImage: {
                xs: 'none',
                md: `linear-gradient(${alpha(gold, 0.16)} 1px, transparent 1px), linear-gradient(to right, ${alpha(gold, 0.16)} 1px, ${bg} 1px)`,
              },
              backgroundSize: '20px 20px',
              gap: 2,
              flexShrink: 0,
            };
          }}
        >
          <Button
            variant="text"
            size="large"
            startIcon={<PhoneIcon />}
            onClick={handleCalendlyDialogOpen}
            aria-label="Schedule a personal case review with Robert Garza"
            sx={(t) => ({
              px: { xs: 1, md: 4 },
              py: 1.5,
              minHeight: 48,
              fontSize: { xs: '0.9375rem', md: '1rem' },
              textTransform: 'none',
              lineHeight: 1.5,
              whiteSpace: 'normal',
              flexShrink: 0,
              color: t.palette.primary.main,
              fontWeight: 700,
              '&:hover': { background: t.palette.mode === 'dark' ? 'rgba(255,215,0,0.08)' : 'rgba(184,134,11,0.08)' },
            })}
          >
            Schedule a Personal Case Review
          </Button>
        </Box>
      </Box>

      {/* Dialog for Get Involved */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          Get Involved
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select an option below that describes your situation:
          </Typography>
          <List>
            <NavigateToButton
              billSlug="a-new-start-at-equality"
              onClick={() => {}}
              sx={{ marginBottom: 2, width: '100%' }}
            >
              A New Start at Equality
            </NavigateToButton>
            <NavigateToButton
              billSlug="time-taken-time-back"
              onClick={() => {}}
              sx={{ marginBottom: 2, width: '100%' }}
            >
              Time Taken, Time Back
            </NavigateToButton>
            <NavigateToButton billSlug="three-strikes" onClick={() => {}} sx={{ marginBottom: 2, width: '100%' }}>
              Three Strikes Law
            </NavigateToButton>
            <NavigateToButton
              billSlug="justice-delayed-is-justice-denied"
              onClick={() => {}}
              sx={{ marginBottom: 2, width: '100%' }}
            >
              Justice Delayed, Justice Denied
            </NavigateToButton>
            <NavigateToButton
              billSlug="cameras-in-courtrooms"
              onClick={() => {}}
              sx={{ marginBottom: 2, width: '100%' }}
            >
              Cameras in Courtroom
            </NavigateToButton>
            <NavigateToButton
              billSlug="child-support-dual-calculation-reform"
              onClick={() => {}}
              sx={{ marginBottom: 2, width: '100%' }}
            >
              Child Support Reform
            </NavigateToButton>
            <NavigateToButton
              billSlug="appealable-temporary-orders"
              onClick={() => {}}
              sx={{ marginBottom: 2, width: '100%' }}
            >
              Appealable Temporary Orders
            </NavigateToButton>
            <NavigateToButton
              billSlug="litigant-access-economy-act"
              onClick={() => {}}
              sx={{ marginBottom: 2, width: '100%' }}
            >
              The Litigant Access and Economy Act
            </NavigateToButton>
          </List>
        </DialogContent>
      </Dialog>

      {/* About Robert Garza */}
      <Box
        sx={{
          py: { xs: 8, md: 20 },
          px: { xs: 4 },
          marginX: { xs: -2, sm: -3 },
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]),
        }}
      >
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={HeadShot}
                alt="Robert Garza"
                sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Meet Robert Garza
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Robert Garza is a nationally recognized advocate for parental rights and family court reform. After
                enduring 43+ false CPS allegations in a 15-year custody battle, he turned personal adversity into a
                mission for justice.
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                As co-executive producer of{' '}
                <Link
                  component="a"
                  href="https://www.youtube.com/watch?v=H_z0AfxT2bE"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  aria-label="I stand with John Documentary"
                >
                  <em>I Stand with John</em>
                </Link>{' '}
                and a featured advocate in{' '}
                <Link
                  component="a"
                  href="https://www.youtube.com/watch?v=dTxqEFTBgJM"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  aria-label="We the Parents Documentary"
                >
                  <em>We the Parents</em>
                </Link>
                , Robert raises awareness and drives change globally.
              </Typography>
              <Button
                variant="contained"
                size="large"
                href="/about-robert-garza"
                onClick={() => trackEvent('cta_click', 'engagement', 'Learn More About Robert - Home')}
                sx={{ px: 4, py: 2 }}
              >
                Learn More
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Achievements */}
      <Box sx={{ py: { xs: 8, md: 20 }, px: { xs: 4 }, marginX: { xs: -2, sm: -3 } }}>
        <Container>
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            A Proven Leader in Advocacy
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
              <Card sx={{ textAlign: 'center', boxShadow: 3, width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Legislative Impact
                  </Typography>
                  <Typography variant="body2">
                    Instrumental in passing Texas Senate Bill 718—“Time Taken, Time Back”—and the “Three Strikes Law”
                    for custody interference.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
              <Card sx={{ textAlign: 'center', boxShadow: 3, width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                    National Reach
                  </Typography>
                  <Typography variant="body2">
                    Traveled to 40+ states lobbying legislators and spoken at Capitol Hill and CPAC on fatherlessness
                    and parental alienation.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
              <Card sx={{ textAlign: 'center', boxShadow: 3, width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Media Presence
                  </Typography>
                  <Typography variant="body2">
                    Featured in documentaries and major press outlets, amplifying the call for family court reform.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Legislative Bills */}
      <Box
        component="section"
        aria-labelledby="home-bills-heading"
        sx={{
          py: { xs: 7, md: 10 },
          px: 0,
          marginX: { xs: -2, sm: -3 },
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]),
          '& .MuiCard-root': {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 3px 16px rgba(0,0,0,0.04)',
            '&:focus-within': { borderColor: 'text.primary' },
          },
          '& .MuiCardMedia-root': {
            display: 'block',
            width: '100%',
            height: 'auto',
            aspectRatio: '4 / 3',
            objectFit: 'contain',
            objectPosition: 'center',
            p: 2,
            backgroundColor: '#fff',
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiCardContent-root': {
            p: { xs: 2.5, sm: 3 },
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            '&:last-child': { pb: { xs: 2.5, sm: 3 } },
          },
          '& .MuiCardContent-root h3': {
            fontSize: { xs: '1.25rem', lg: '1.375rem' },
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: '-0.015em',
            overflowWrap: 'anywhere',
            mb: 1.5,
          },
          '& .MuiCardContent-root p': {
            overflowWrap: 'anywhere',
            fontSize: '1rem',
            lineHeight: 1.65,
            color: 'text.secondary',
            mb: 3,
          },
          '& .MuiCardContent-root .MuiButton-root': {
            minHeight: 48,
            mt: 'auto',
            px: 0,
            py: 1.25,
            border: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            color: 'text.primary',
            fontSize: '0.9375rem',
            textTransform: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            '&:hover': { color: 'text.primary', backgroundColor: 'action.hover', borderColor: 'divider' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'text.primary', outlineOffset: 4 },
          },
        }}
      >
        <Container sx={{ px: { xs: 3, sm: 4 } }}>
          <Box sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
            <Typography
              component="p"
              sx={{ fontSize: '0.75rem', letterSpacing: '0.12em', fontWeight: 700, color: 'text.secondary', mb: 1.5 }}
            >
              THE LEGISLATIVE AGENDA
            </Typography>
            <Typography
              id="home-bills-heading"
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textWrap: 'balance',
                mb: 2,
              }}
            >
              Support the Family Court Reform Bill Stack
            </Typography>
            <Typography sx={{ fontSize: '1rem', lineHeight: 1.65, color: 'text.secondary' }}>
              Explore the proposals, understand what each changes, and find the reforms that matter to your family.
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia component="img" image={FiftyFiftyCustody} alt="50/50 Custody" loading="lazy" />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    A New Start at Equality
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Establishes equal custody as the starting point, ensuring both parents have meaningful relationships
                    with their children.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: A New Start at Equality"
                    onClick={() => {}}
                    billSlug="a-new-start-at-equality"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia component="img" image={TimeTakenTimeBack} alt="Time Taken, Time Back" loading="lazy" />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Time Taken, Time Back
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Restores parenting time lost due to unfounded allegations or precautionary removals.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Time Taken, Time Back"
                    onClick={() => {}}
                    billSlug="time-taken-time-back"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia component="img" image={ThreeStrikes} alt="Three Strikes Law" loading="lazy" />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Three Strikes
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Enhanced penalties for repeat violators ensure accountability and protect families from ongoing
                    court abuse.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Three Strikes"
                    onClick={() => {}}
                    billSlug="three-strikes"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={JusticeDelayedJusticeDenied}
                  alt="Justice Delayed, Justice Denied"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Justice Delayed is Justice Denied
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Mandatory timelines for family court proceedings prevent prolonged cases that harm children and
                    families.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Justice Delayed is Justice Denied"
                    onClick={() => {}}
                    billSlug="justice-delayed-is-justice-denied"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia component="img" image={CamerasInCourtroom} alt="Cameras in Courtroom" loading="lazy" />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Cameras in Courtrooms
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Mandatory recording in family courtrooms ensures transparency, protects constitutional rights, and
                    prevents judicial misconduct.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Cameras in Courtrooms"
                    onClick={() => {}}
                    billSlug="cameras-in-courtrooms"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia component="img" image={ChildSupportReform} alt="Child Support Reform" loading="lazy" />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Child Support Dual Calculation Reform
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Reform child support calculations to be fair, transparent, and consider both parents' circumstances
                    and parenting time.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Child Support Dual Calculation Reform"
                    billSlug="child-support-dual-calculation-reform"
                    onClick={() => {}}
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={AppealableTemporaryOrders}
                  alt="Appealable Temporary Orders"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Appealable Temporary Orders
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Ensures temporary custody and support orders can be appealed, preventing irreversible harm from
                    flawed interim decisions.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Appealable Temporary Orders"
                    onClick={() => {}}
                    billSlug="appealable-temporary-orders"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={LitigantAccessEconomy}
                  alt="The Litigant Access and Economy Act"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    The Litigant Access and Economy Act
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Improves access to justice by reducing court costs and streamlining procedures for family law cases.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: The Litigant Access and Economy Act"
                    onClick={() => {}}
                    billSlug="litigant-access-economy-act"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={ReimbursementFees}
                  alt="Reimbursement of Fees for Parental Alienating Behavior"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Reimbursement of Fees for Parental Alienating Behavior
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Holds alienating parents accountable by requiring them to reimburse legal fees caused by their
                    obstructive behavior.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Reimbursement of Fees for Parental Alienating Behavior"
                    onClick={() => {}}
                    billSlug="reimbursement-fees-parental-alienation"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={FamilyRightsDueProcess}
                  alt="The Family Rights and Due Process Act"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    The Family Rights and Due Process Act
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Establishes comprehensive protections for fundamental constitutional rights in family court
                    proceedings, ensuring due process and equal protection.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: The Family Rights and Due Process Act"
                    onClick={() => {}}
                    billSlug="family-rights-due-process-act"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={ChildSupportIntegrity}
                  alt="Child Support Integrity Act"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Child Support Integrity Act
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Establishes DNA testing rights and transparency in child support enforcement, ensuring biological
                    accuracy and fair practices for all families.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Child Support Integrity Act"
                    onClick={() => {}}
                    billSlug="child-support-integrity-act"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={BestInterestChild}
                  alt="Best Interest of the Child Definition Act"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Best Interest of the Child Definition Act
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Establishes a clear statutory definition of "best interest of the child," ending arbitrary judicial
                    interpretations and ensuring consistent, fair custody determinations for all families.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Best Interest of the Child Definition Act"
                    onClick={() => {}}
                    billSlug="best-interest-of-the-child-definition-act"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={ParentChildRestoration}
                  alt="Parent and child reunited beside broken chains and scales of justice, representing the Parent-Child Restoration Act"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Parent-Child Restoration Act
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Requires courts to promptly lift unsupported custody or visitation restrictions, restore the prior
                    parenting order, and award compensatory parenting time while preserving necessary child protections.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Parent-Child Restoration Act"
                    onClick={() => {}}
                    billSlug="parent-child-restoration-act"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
              <Card component="article">
                <CardMedia
                  component="img"
                  image={JudicialAccountabilityReferral}
                  alt="Family dismissed by a judge while an appellate clerk refers an opinion to independent commissioners, representing the Judicial Accountability Referral Act"
                  loading="lazy"
                />
                <CardContent>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
                    Judicial Accountability Referral Act
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                    Proposes a referral pathway that brings judicial conduct concerns from family court proceedings
                    before an independent review commission, giving dismissed families a real path to accountability.
                  </Typography>
                  <NavigateToButton
                    aria-label="Read bill: Judicial Accountability Referral Act"
                    onClick={() => {}}
                    billSlug="judicial-accountability-referral-act"
                    variant="text"
                    sx={{ width: '100%' }}
                  >
                    Read bill
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Personal Case Review */}
      <Box sx={{ py: { xs: 8, md: 20 }, px: { xs: 4 }, marginX: { xs: -2, sm: -3 } }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={HeadShot3}
                alt="Robert Garza"
                sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Schedule a Personal Case Review
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Meet with Robert one-on-one over video to talk through your situation. He’ll walk you through custody,
                visitation, and court reform issues from the perspective of a parent who has lived it — and leave you
                with clear, practical next steps.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleCalendlyDialogOpen}
                sx={{ px: 4, py: 2 }}
                aria-label="Book a personal case review with Robert Garza"
              >
                BOOK NOW
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Affiliations */}
      <Box
        sx={{
          py: { xs: 8, md: 20 },
          px: { xs: 4 },
          marginX: { xs: -2, sm: -3 },
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]),
        }}
      >
        <Container>
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            Proudly Affiliated With
          </Typography>
          <Grid container spacing={4} justifyContent="center" direction="row">
            <Grid item xs={12} sm={3} textAlign="center">
              <Box
                component="img"
                src={AffiliateLogoOne}
                alt="Texans for Judicial Accountability"
                loading="lazy"
                sx={{ mb: 2, maxWidth: '200px', width: '100%', height: 'auto', display: 'block', mx: 'auto' }}
              />
              <Typography variant="body2">Texans for Judicial Accountability</Typography>
            </Grid>
            <Grid item xs={12} sm={3} textAlign="center">
              <Box
                component="img"
                src={AffiliateLogoTwo}
                alt="National Parents Organization"
                loading="lazy"
                sx={{ mb: 2, maxWidth: '200px', width: '100%', height: 'auto', display: 'block', mx: 'auto' }}
              />
              <Typography variant="body2">National Parents Organization</Typography>
            </Grid>
            <Grid item xs={12} sm={3} textAlign="center">
              <Box
                component="img"
                src={AffiliateLogoThree}
                alt="The John Mast Foundation"
                loading="lazy"
                sx={{ mb: 2, maxWidth: '200px', width: '100%', height: 'auto', display: 'block', mx: 'auto' }}
              />
              <Typography variant="body2">The John Mast Foundation</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Donation Dialog */}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <DonationDialog
          open={donationDialogOpen}
          onClose={handleDonationDialogClose}
          onOpen={() => setDonationDialogOpen(true)}
        />
      </Box>

      {/* Join The Team Dialog */}
      <JoinTheTeamDialog open={joinTeamDialogOpen} onClose={handleJoinTeamDialogClose} />

      {/* Calendly Dialog */}
      {calendlyDialogOpen && (
        <React.Suspense fallback={<div />}>
          <CalendlyDialog
            open={calendlyDialogOpen}
            onClose={handleCalendlyDialogClose}
            calendlyUrl="https://calendly.com/robert-garza-us"
          />
        </React.Suspense>
      )}
    </Container>
  );
};

export default Home;
